//======================================================================================================
//
// This store controls the application layout to the left, bottom and right side of the visualizer stage.
// It will be linked to the users preferences and be able to be restored from api or localStorage.
//
//======================================================================================================

import { defineStore } from "pinia";
import {
	GetNewId,
	RetrieveElementFromLayout,
	RetrieveParentElementFromLayout,
	RemoveElementFromChildren,
	AddElementToStack,
	FindNode,
	UpdateFlex,
	CleanLayout,
	AddElementToRow,
	CollapsePanel
} from "../utils/LayoutHelper";
import { GetGuid } from "@/utils/Utilities";
import { usePvwStore } from "./ssaStore/pvw";

const initPanelState: LayoutType = {
	id: 0,
	component: "",
	type: "",
	componentState: {
		flex: 1,
		lastFlex: 1,
		color: ""
	},
	componentName: "",
	children: []
};
const initialToolbeltPosition: string = localStorage.getItem("toolbelt-position") || "left";
const initialToolHelperPosition: string[] =
	JSON.parse(JSON.stringify(localStorage.getItem("toolhelper-position"))) || [];

export const useLayoutStore = defineStore("layoutStore", {
	state: () =>
		({
			panelLayout: initPanelState,
			isDragging: false,
			toolbeltPos: initialToolbeltPosition,
			toolhelperPos: initialToolHelperPosition
		}) as any,
	actions: {
		SET_TOOLHELPER_POSITION(value: string[]) {
			this.toolhelperPos = value;
		},
		SET_TOOLBELT_POSITION(value: string) {
			this.toolbeltPos = value;
		},
		SET_IS_DRAGGING(value: boolean) {
			this.isDragging = value;
		},
		SET_PANEL_LAYOUT(payload: LayoutType) {
			this.panelLayout = payload;
		},
		async SPLIT_PANEL(payload: { panel1: number; type: string; clone: boolean }) {
			const PANEL1: number = payload.panel1;
			const LAYOUT: LayoutType = this.panelLayout;
			const P1_OBJ: LayoutType | void = FindNode(PANEL1, this.panelLayout.children);
			const COMP_DESTINATION_PARENT: LayoutType | void = RetrieveParentElementFromLayout(
				this.panelLayout,
				PANEL1
			);
			const COMP_DESTINATION_PARENT_PARENT: LayoutType | void =
				RetrieveParentElementFromLayout(
					this.panelLayout,
					Number(COMP_DESTINATION_PARENT!.id)
				);
			let newComp: any;
			let cloneId = null;
			if (payload.clone) {
				cloneId = await usePvwStore().CLONE_VIEW();
			}
			console.log("cloneId", cloneId);

			if (payload.type == "v") {
				if (COMP_DESTINATION_PARENT_PARENT!.type == "column") {
					newComp = {
						id: GetNewId(),
						type: "row",
						componentState: {
							flex: COMP_DESTINATION_PARENT!.componentState.flex,
							lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
							color: ""
						},
						children: [
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: {
									flex:
										COMP_DESTINATION_PARENT!.componentState.flex < 2
											? 1
											: COMP_DESTINATION_PARENT!.componentState.flex / 2,
									lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
									color: ""
								},
								type: "stacked",
								children: [
									Object.assign({ ...P1_OBJ, id: GetNewId() }) as LayoutType
								]
							},
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: {
									flex:
										COMP_DESTINATION_PARENT!.componentState.flex < 2
											? 1
											: COMP_DESTINATION_PARENT!.componentState.flex / 2,
									lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
									color: ""
								},
								type: "stacked",
								children: [
									Object.assign({ ...P1_OBJ }, { id: GetNewId() }) as LayoutType
								]
							}
						]
					};
					AddElementToRow(
						LAYOUT,
						COMP_DESTINATION_PARENT_PARENT!.id,
						COMP_DESTINATION_PARENT!.id,
						newComp,
						"stack",
						this.panelLayout
					);
					this.REMOVE_COMPONENT({ id: PANEL1 });
					CleanLayout(LAYOUT);
				} else {
					newComp = {
						id: GetNewId(),
						type: "row",

						componentState: {
							flex:
								COMP_DESTINATION_PARENT!.componentState.flex < 1
									? 1
									: COMP_DESTINATION_PARENT!.componentState.flex,
							lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
							color: ""
						},
						children: [
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: {
									flex: 1,
									lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
									color: ""
								},
								type: "stacked",
								children: [
									Object.assign({ ...P1_OBJ, id: GetNewId() }) as LayoutType
								]
							},
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: {
									flex: 1,
									lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
									color: ""
								},
								type: "stacked",
								children: [
									Object.assign({ ...P1_OBJ }, { id: GetNewId() }) as LayoutType
								]
							}
						]
					};
					COMP_DESTINATION_PARENT!.componentState.flex =
						COMP_DESTINATION_PARENT!.componentState.flex;
					AddElementToRow(
						LAYOUT,
						COMP_DESTINATION_PARENT_PARENT!.id,
						COMP_DESTINATION_PARENT!.id,
						newComp,
						"stack",
						this.panelLayout
					);
					this.REMOVE_COMPONENT({ id: PANEL1 });
					CleanLayout(LAYOUT);
				}
			} else {
				newComp = {
					id: GetNewId(),
					type: "column",

					componentState: {
						flex:
							COMP_DESTINATION_PARENT!.componentState.flex < 1
								? 1
								: COMP_DESTINATION_PARENT!.componentState.flex,
						lastFlex: COMP_DESTINATION_PARENT!.componentState.flex,
						color: ""
					},
					children: [
						{
							id: GetNewId(),
							componentName: "",
							component: "",
							componentState: { flex: 1, lastFlex: 1, color: "" },
							type: "stacked",
							children: [Object.assign({ ...P1_OBJ, id: GetNewId() }) as LayoutType]
						},
						{
							id: GetNewId(),
							componentName: "",
							component: "",
							componentState: { flex: 1, lastFlex: 1, color: "" },
							type: "stacked",
							children: [
								Object.assign({ ...P1_OBJ }, { id: GetNewId() }) as LayoutType
							]
						}
					]
				};

				AddElementToRow(
					LAYOUT,
					COMP_DESTINATION_PARENT_PARENT!.id,
					COMP_DESTINATION_PARENT!.id,
					newComp,
					"stack",
					this.panelLayout
				);
				this.REMOVE_COMPONENT({ id: PANEL1 });
				CleanLayout(LAYOUT);
			}
		},
		RESIZE_PANEL(payload: { panel1: number; panel2: number; type: string }) {
			this.isDragging = true;
			const PANEL1: number = payload.panel1;
			const PANEL2: number = payload.panel2;
			const TYPE: string = payload.type;
			const P1_EL: HTMLElement = document.getElementById(PANEL1.toString()) as HTMLElement;
			const P2_EL: HTMLElement = document.getElementById(PANEL2.toString()) as HTMLElement;
			const P1_OBJ: LayoutType | void = FindNode(PANEL1, this.panelLayout.children);
			const P2_OBJ: LayoutType | void = FindNode(PANEL2, this.panelLayout.children);
			if (P1_OBJ == null || P2_OBJ == null) return;
			const PANEL1_OFFSET = P1_EL.getBoundingClientRect();
			const PANEL2_OFFSET = P2_EL.getBoundingClientRect();
			const TOTAL_FLEX_SIZE: number = P1_OBJ.componentState.flex + P2_OBJ.componentState.flex;

			const TOTAL_WIDTH: number = PANEL1_OFFSET.width + PANEL2_OFFSET.width;
			const TOTAL_HEIGHT: number = PANEL1_OFFSET.height + PANEL2_OFFSET.height;

			const getAmountOfHorizontalChildren: any = (obj: any, count: number) => {
				if (obj.children && obj.children.length > 0) {
					for (let i = 0; i < obj.children.length; i++) {
						if (obj.children[i].type == "row") {
							count += getAmountOfHorizontalChildren(obj.children[i], count);
						}
						if (obj.children[i].type == "column") {
							count += 1;
						}
						if (obj.children[i].type == "stacked") {
							count += 1;
						}
					}
					return count;
				} else {
					return count;
				}
			};
			const getAmountOfVerticalChildren: any = (obj: any, count: number) => {
				if (obj.children && obj.children.length > 0) {
					for (let i = 0; i < obj.children.length; i++) {
						if (obj.children[i].type == "column") {
							// count += obj.children[i].children.length
							count += getAmountOfVerticalChildren(obj.children[i], count);
						}
						if (obj.children[i].type == "row") {
							count += 1;
						}
						if (obj.children[i].type == "stacked") {
							count += 1;
						}
					}
					return count;
				} else {
					return count;
				}
			};

			const child1Count = getAmountOfHorizontalChildren(P1_OBJ, 0);
			const child2Count = getAmountOfHorizontalChildren(P2_OBJ, 0);

			const child1CountV = getAmountOfVerticalChildren(P1_OBJ, 0);
			const child2CountV = getAmountOfVerticalChildren(P2_OBJ, 0);

			const MIN_WIDTH1: number =
				(TOTAL_FLEX_SIZE / TOTAL_WIDTH) * (P1_OBJ.type == "row" ? 250 * child1Count : 250);
			const MIN_WIDTH2: number =
				(TOTAL_FLEX_SIZE / TOTAL_WIDTH) * (P2_OBJ.type == "row" ? 250 * child2Count : 250);
			const MIN_HEIGHT1: number =
				(TOTAL_FLEX_SIZE / TOTAL_HEIGHT) *
				(P1_OBJ.type == "column" ? 150 * child1CountV : 150);
			const MIN_HEIGHT2: number =
				(TOTAL_FLEX_SIZE / TOTAL_HEIGHT) *
				(P2_OBJ.type == "column" ? 150 * child2CountV : 150);

			const NEW_PANEL1_OFFSET = P1_EL.getBoundingClientRect();
			const mousemoveListener = (e: MouseEvent) => {
				const X: number = (e.pageX - NEW_PANEL1_OFFSET.left) / TOTAL_WIDTH;
				const Y: number = (e.pageY - NEW_PANEL1_OFFSET.top) / TOTAL_HEIGHT;
				let increment = X;
				if (TYPE == "column") {
					increment = Y;
					if (
						TOTAL_FLEX_SIZE * increment > MIN_HEIGHT1 &&
						TOTAL_FLEX_SIZE * (1 - increment) > MIN_HEIGHT2
					) {
						UpdateFlex(this.panelLayout.children, PANEL1, TOTAL_FLEX_SIZE * increment);
						UpdateFlex(
							this.panelLayout.children,
							PANEL2,
							TOTAL_FLEX_SIZE * (1 - increment)
						);
					}
				} else {
					if (
						TOTAL_FLEX_SIZE * increment > MIN_WIDTH1 &&
						TOTAL_FLEX_SIZE * (1 - increment) > MIN_WIDTH2
					) {
						UpdateFlex(this.panelLayout.children, PANEL1, TOTAL_FLEX_SIZE * increment);
						UpdateFlex(
							this.panelLayout.children,
							PANEL2,
							TOTAL_FLEX_SIZE * (1 - increment)
						);
					}
				}
			};
			const mouseupListener = () => {
				this.isDragging = false;
				window.removeEventListener("mousemove", mousemoveListener);
				window.removeEventListener("mouseup", mouseupListener);
			};
			window.addEventListener("mousemove", mousemoveListener);
			window.addEventListener("mouseup", mouseupListener);
		},
		REMOVE_COMPONENT(payload: { id: number }) {
			const LAYOUT: LayoutType = this.panelLayout;
			LAYOUT.children = RemoveElementFromChildren(this.panelLayout.children, payload.id);
			CleanLayout(LAYOUT);
		},
		COLLAPSE_PANEL(payload: { type: string; id: number; id2: number }) {
			const LAYOUT: LayoutType = this.panelLayout;
			CollapsePanel(LAYOUT, payload.type, payload.id, payload.id2);
		},
		MOVE_COMPONENT(payload: { type: string; id: number; dropEl: number }) {
			const TYPE: string = payload.type;
			const COMP_TO_MOVE: LayoutType | void = RetrieveElementFromLayout(
				this.panelLayout,
				payload.id
			);
			const COMP_DESTINATION: LayoutType | void = RetrieveElementFromLayout(
				this.panelLayout,
				payload.dropEl
			);
			const COMP_DESTINATION_PARENT: LayoutType | void = RetrieveParentElementFromLayout(
				this.panelLayout,
				Number(payload.dropEl)
			);
			const COMP_DESTINATION_PARENT2: LayoutType | void = RetrieveParentElementFromLayout(
				this.panelLayout,
				Number(payload.id)
			);
			const LAYOUT: LayoutType = this.panelLayout;
			window.dispatchEvent(new Event("resize"));
			if (TYPE == "right-zone") {
				LAYOUT.children.push({
					id: GetGuid(),
					type: "column",
					componentName: "",
					componentState: {
						flex: 1,
						lastFlex: 1
					},
					children: [
						{
							id: GetNewId(),
							componentName: "",
							component: "",
							componentState: { flex: 1, lastFlex: 1, color: "" },
							type: "stacked",
							children: [
								{
									...COMP_TO_MOVE,
									componentState: { ...COMP_TO_MOVE!.componentState, flex: 1 },
									id: GetNewId()
								}
							]
						}
					]
				} as any);
				LAYOUT.children = RemoveElementFromChildren(
					this.panelLayout.children,
					COMP_TO_MOVE!.id
				);
				CleanLayout(LAYOUT);
			}
			if (TYPE == "left-zone") {
				LAYOUT.children.unshift({
					id: GetGuid(),
					type: "column",
					componentName: "",
					componentState: {
						flex: 1,
						lastFlex: 1
					},
					children: [
						{
							id: GetNewId(),
							componentName: "",
							component: "",
							componentState: { flex: 1, lastFlex: 1, color: "" },
							type: "stacked",
							children: [
								{
									...COMP_TO_MOVE,
									componentState: { ...COMP_TO_MOVE!.componentState, flex: 1 },
									id: GetNewId()
								}
							]
						}
					]
				} as any);
				LAYOUT.children = RemoveElementFromChildren(
					this.panelLayout.children,
					COMP_TO_MOVE!.id
				);
				CleanLayout(LAYOUT);
			}
			if (TYPE == "bottom-zone") {
				const newComp: LayoutType = {
					id: GetNewId(),
					type: "column",
					componentName: "",
					component: "",
					componentState: {
						flex: 1,
						lastFlex: 1,
						color: ""
					},
					children: [
						{
							id: GetNewId(),
							componentName: "",
							component: "",
							componentState: { flex: 1, lastFlex: 1, color: "" },
							type: "stacked",
							children: [
								{
									...COMP_TO_MOVE,
									componentState: { ...COMP_TO_MOVE!.componentState, flex: 1 },
									id: GetNewId()
								}
							]
						},
						{
							...COMP_DESTINATION,
							componentState: { ...COMP_DESTINATION!.componentState, flex: 3 },
							id: GetNewId()
						}
					]
				} as any;
				newComp.children.reverse();
				LAYOUT.children = RemoveElementFromChildren(this.panelLayout.children, payload.id);
				AddElementToRow(
					LAYOUT,
					COMP_DESTINATION_PARENT!.id,
					COMP_DESTINATION!.id,
					newComp,
					"bottom",
					this.panelLayout
				);
				LAYOUT.children = RemoveElementFromChildren(
					this.panelLayout.children,
					COMP_DESTINATION!.id
				);
				LAYOUT.children = RemoveElementFromChildren(
					this.panelLayout.children,
					COMP_TO_MOVE!.id
				);
				CleanLayout(LAYOUT);
			}
			if (
				COMP_DESTINATION_PARENT2 == null ||
				COMP_TO_MOVE == null ||
				COMP_DESTINATION == null ||
				COMP_DESTINATION_PARENT == null
			) {
				return;
			}

			if (TYPE == "stack") {
				if (LAYOUT.children.length == 1 || COMP_DESTINATION_PARENT2.children.length == 1) {
					if (COMP_DESTINATION_PARENT2.id == payload.dropEl) return;
				}
				LAYOUT.children = RemoveElementFromChildren(this.panelLayout.children, payload.id);
				CleanLayout(LAYOUT);
				LAYOUT.children = AddElementToStack(LAYOUT.children, payload.dropEl, COMP_TO_MOVE);
				this.panelLayout = LAYOUT;

				return;
			}
			if (TYPE == "bottom" || TYPE == "top") {
				const newComp: LayoutType = {
					id: GetNewId(),
					type: "column",
					componentName: "",
					component: "",
					componentState: {
						flex: 1,
						lastFlex: 1,
						color: ""
					},
					children: [
						{
							id: GetNewId(),
							componentName: "",
							component: "",
							componentState: { flex: 1, lastFlex: 1, color: "" },
							type: "stacked",
							children: [
								{
									...COMP_TO_MOVE,
									componentState: { ...COMP_TO_MOVE.componentState, flex: 1 },
									id: GetNewId()
								}
							]
						},
						{
							...COMP_DESTINATION,
							componentState: { ...COMP_DESTINATION.componentState, flex: 1 },
							id: GetNewId()
						}
					]
				};
				if (TYPE == "bottom") {
					if (COMP_DESTINATION_PARENT2.children.length == 1) {
						if (COMP_DESTINATION_PARENT2.id == payload.dropEl) return;
					}

					newComp.children.reverse();
				}
				LAYOUT.children = RemoveElementFromChildren(this.panelLayout.children, payload.id);
				AddElementToRow(
					LAYOUT,
					COMP_DESTINATION_PARENT.id,
					COMP_DESTINATION.id,
					newComp,
					TYPE,
					this.panelLayout
				);
				LAYOUT.children = RemoveElementFromChildren(
					this.panelLayout.children,
					COMP_DESTINATION.id
				);
				LAYOUT.children = RemoveElementFromChildren(
					this.panelLayout.children,
					COMP_TO_MOVE.id
				);
				CleanLayout(LAYOUT);
			}
			if (TYPE == "left" || TYPE == "right") {
				const PARENT_TYPE: string = COMP_DESTINATION_PARENT.type;
				let newComp: LayoutType = {
					id: GetNewId(),
					type: "stacked",
					componentName: "",
					component: "",
					componentState: {
						flex: 1,
						lastFlex: 1,
						color: ""
					},
					children: [COMP_TO_MOVE]
				};
				if (PARENT_TYPE == "root") {
					LAYOUT.children = RemoveElementFromChildren(
						this.panelLayout.children,
						payload.id
					);
					AddElementToRow(
						LAYOUT,
						COMP_DESTINATION_PARENT.id,
						COMP_DESTINATION.id,
						newComp,
						TYPE,
						this.panelLayout
					);
				}
				if (PARENT_TYPE == "row") {
					LAYOUT.children = RemoveElementFromChildren(
						this.panelLayout.children,
						payload.id
					);
					AddElementToRow(
						LAYOUT,
						COMP_DESTINATION_PARENT.id,
						COMP_DESTINATION.id,
						newComp,
						TYPE,
						this.panelLayout
					);
				}
				if (PARENT_TYPE == "column" && COMP_DESTINATION.type == "stacked") {
					newComp = {
						id: GetNewId(),
						type: "row",
						componentName: "",
						component: "",
						componentState: {
							flex: 1,
							lastFlex: 1,
							color: ""
						},
						children: [
							{
								id: GetNewId(),
								componentName: "",
								component: "",
								componentState: { flex: 1, color: "#fff", lastFlex: 1 },
								type: "stacked",
								children: [
									{
										...COMP_TO_MOVE,
										componentState: { ...COMP_TO_MOVE.componentState, flex: 1 },
										id: GetNewId()
									}
								]
							},
							{
								...COMP_DESTINATION,
								componentState: { ...COMP_TO_MOVE.componentState, flex: 1 },
								id: GetNewId()
							}
						]
					};
					if (TYPE == "right") {
						newComp.children.reverse();
					}

					if (TYPE == "left") {
						AddElementToRow(
							LAYOUT,
							COMP_DESTINATION_PARENT.id,
							payload.id,
							newComp,
							TYPE,
							this.panelLayout
						);
					}

					AddElementToRow(
						LAYOUT,
						COMP_DESTINATION_PARENT.id,
						COMP_DESTINATION.id,
						newComp,
						TYPE,
						this.panelLayout
					);
					LAYOUT.children = RemoveElementFromChildren(
						this.panelLayout.children,
						payload.id
					);
					LAYOUT.children = RemoveElementFromChildren(
						this.panelLayout.children,
						COMP_DESTINATION.id
					);
				}
			}

			CleanLayout(LAYOUT);
		}
	}
});
